import { EFeatureFlags } from 'constants/featureFlags'
import { PlayProfileRole } from 'constants/play-profile'
import { AuthRoleNameEnum } from 'constants/user'
import routes from 'routes'

// IMPORTANT: When adding new entries to SIDEBAR_ITEMS / adminSidebarItems below,
// set `allowedProfileTypes` for any item that should appear on the play routes
// (/play-dashboard, /play-profile). Items without `allowedProfileTypes` are
// considered "org-only" and will be hidden whenever an activeProfileType is in
// context. Skipping the field silently drops the item from every play profile.
export interface SidebarItem {
  path: string
  name: string
  iconName?: string
  header?: string
  badge?: {
    variant: string
    text: string
  }
  // Org-membership role guard (existing). Empty/undefined = visible to all roles.
  allowedRoles?: AuthRoleNameEnum[]
  // Play-profile-type guard (new). When set AND an activeProfileType is in
  // context (play routes), the item must list the current type to render.
  // Undefined = visible on org routes only — hidden on play routes.
  allowedProfileTypes?: PlayProfileRole[]
  hideForViewer?: boolean
  featureFlag?: EFeatureFlags
  // Renders the row greyed out and non-navigating. Used for nav entries whose
  // destination page does not exist yet, so the sidebar can already match the
  // shipped design without dead links.
  isDisabled?: boolean
  children?: SidebarItem[]
}

// Convenience: every profile type passes (Dashboard, Schedule).
const ALL_PROFILES: PlayProfileRole[] = ['player', 'child', 'coach']

// Staff who manage org content: stacks, plans, collections, assignments, schedule.
const ADMIN_ROLES = [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH]
// Staff who manage the org itself: its settings and its membership. Per the
// role-permission matrix a Coach is deliberately excluded, which is why Manage
// your Org and Player Groups are Owner/Administrator only.
const ORG_ADMIN_ROLES = [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN]
// Reporting is narrower still — Owner only, not Administrator.
const OWNER_ONLY_ROLES = [AuthRoleNameEnum.OWNER]

export enum ERouteName {
  DASHBOARD = 'Dashboard',
  REPORT = 'Reports',
  COLLECTION = 'Collections',
  DRAFT = 'Drafts',
  MESSAGE = 'Messages',
  NOTIFICATION = 'Notifications',
  USER = 'Team Members',
  GROUP = 'User Groups',
  ARCHIVE = 'Archives',
  COMMON_LIBRARY = 'Common Library',
  SETTING = 'Settings',
  DOCUMENT = 'Documents',
  OPERATION = 'Operations',
  ICON_BUILDER = 'Icon Builder',
  TAG = 'Tags',
  METRIC = 'Metrics',
  BILLING = 'Billing',
  // APPEARANCE / SSO are intentionally retained as enum identifiers so future
  // greps surface them; their sidebar entries were removed in the cross-team
  // migration and the routes themselves get 404'd in Phase 05.
  APPEARANCE = 'Appearance',
  SSO = 'SSO Configuration'
}

const dashboardRoutes: SidebarItem = {
  path: routes.dashboard.value,
  name: ERouteName.DASHBOARD,
  iconName: 'sidebar-dashboard',
  allowedProfileTypes: ALL_PROFILES
}

const reportRoutes: SidebarItem = {
  path: routes.report.organization.value,
  name: ERouteName.REPORT,
  iconName: 'sidebar-report',
  // Reporting is Owner-only — an Administrator does not get it either.
  allowedRoles: OWNER_ONLY_ROLES,
  allowedProfileTypes: ['coach']
}

const draftRoutes: SidebarItem = {
  path: routes.processes.value,
  hideForViewer: true,
  name: ERouteName.DRAFT,
  iconName: 'sidebar-draft',
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH],
  allowedProfileTypes: ['coach']
}
const collectionRoutes: SidebarItem = {
  path: routes.collections.value,
  name: ERouteName.COLLECTION,
  iconName: 'sidebar-collection',
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH],
  allowedProfileTypes: ['coach']
}

const notificationRoutes: SidebarItem = {
  path: routes.notifications.value,
  name: ERouteName.NOTIFICATION,
  iconName: 'sidebar-notification',
  badge: {
    variant: 'success',
    text: '3'
  },
  allowedProfileTypes: ALL_PROFILES
}

const messageRoutes: SidebarItem = {
  path: routes.messages.value,
  name: ERouteName.MESSAGE,
  iconName: 'sidebar-inbox',
  allowedProfileTypes: ALL_PROFILES
}

const userRoutes: SidebarItem = {
  path: routes.organization.value,
  name: ERouteName.USER,
  iconName: 'sidebar-user',
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH],
  allowedProfileTypes: ['coach']
}

const groupRoutes: SidebarItem = {
  path: routes.groups.value,
  name: ERouteName.GROUP,
  iconName: 'sidebar-group',
  // Group membership is org administration: Owner/Administrator only.
  allowedRoles: ORG_ADMIN_ROLES,
  allowedProfileTypes: ['coach']
}

const archiveRoutes: SidebarItem = {
  path: routes.archive.value,
  name: ERouteName.ARCHIVE,
  iconName: 'sidebar-archive',
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH],
  allowedProfileTypes: ['coach']
}

const plansRoutes: SidebarItem = {
  path: routes.plans.value,
  name: 'Plans',
  iconName: 'sidebar-plans',
  featureFlag: EFeatureFlags.WORK_PLANS,
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH],
  allowedProfileTypes: ['coach']
}

const trainingAssignmentRoutes: SidebarItem = {
  path: routes.plans.trainingAssignments.value,
  name: 'Training Assignments',
  iconName: 'sidebar-plans',
  featureFlag: EFeatureFlags.WORK_PLANS,
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH],
  allowedProfileTypes: ['coach']
}

// Note: no `featureFlag` here — play screens treat this as a placeholder nav
// entry (mobile parity), independent of the org WORK_PLANS rollout flag.
const playerAssignmentRoutes: SidebarItem = {
  path: routes.playerAssignments.value,
  name: 'My Assignments',
  iconName: 'sidebar-plans',
  allowedRoles: [AuthRoleNameEnum.PLAYER],
  allowedProfileTypes: ['player', 'child']
}

const teamsRoutes: SidebarItem = {
  path: routes.teams.value,
  name: 'Teams',
  iconName: 'ic_outline-sports-baseball',
  // Org-management "Teams" page is relevant to every profile (players/children
  // see teams they belong to or are invited to; coaches additionally create).
  allowedProfileTypes: ALL_PROFILES
}

const scheduleRoutes: SidebarItem = {
  path: routes.schedule.value,
  name: 'Schedule',
  iconName: 'mdi_calendar-blank-outline',
  allowedProfileTypes: ALL_PROFILES
}

const commonLibraryRoutes: SidebarItem = {
  path: routes.commonLibrary.value,
  name: ERouteName.COMMON_LIBRARY,
  hideForViewer: true,
  iconName: 'sidebar-library',
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN, AuthRoleNameEnum.COACH],
  allowedProfileTypes: ['coach']
}

const documentRoutes: SidebarItem = {
  path: '/',
  name: ERouteName.DOCUMENT,
  iconName: '',
  // Group header (admin-only) — children carry their own profile-type guards;
  // setting the group itself to 'coach' so the section header hides for play/youth.
  allowedProfileTypes: ['coach'],
  children: [
    draftRoutes,
    collectionRoutes,
    plansRoutes,
    trainingAssignmentRoutes,
    playerAssignmentRoutes,
    scheduleRoutes,
    archiveRoutes
    // commonLibraryRoutes hidden in cross-team migration (same treatment as
    // APPEARANCE + SSO). Route 404s; sidebar entry removed.
  ]
}

const operationRoutes: SidebarItem = {
  path: '/',
  name: ERouteName.OPERATION,
  iconName: '',
  allowedProfileTypes: ['coach'],
  children: [reportRoutes, userRoutes, groupRoutes]
}

// ---------------------------------------------------------------------------
// Org Admin (Programing Admin) sidebar
// ---------------------------------------------------------------------------
// The consts declared in this block are admin-only and are NOT referenced by
// `knowHowRoutes` below — Stacks, Player Groups, Assignments and Reports are clones
// precisely so the basic-user / play-profile sidebar keeps its legacy labels and
// icons. Everything else in the admin groups (`dashboardRoutes`, `plansRoutes`,
// `collectionRoutes`, `teamsRoutes`, `scheduleRoutes`) is a SHARED object: editing
// one of those changes both sidebars.
//
// `adminSidebarItems` is also read by `useRoleRouteGuard` (route authorization)
// and by the mobile `BottomNav`, so an item's `path` / `allowedRoles` / `isDisabled`
// are behavioural, not decorative.
// "Stacks" is the search screen — it is the list of the user's stacks (same
// destination as the Topbar "My Stacks" shortcut).
//
// Open to every org role, Player included: a Player reads the Organization's
// published Stacks (view-only — the page itself withholds every authoring action
// from them). Listed in both sidebar sets for that reason, and it carries the
// 'player' profile type so the entry survives the play-profile guard.
const stacksRoutes: SidebarItem = {
  path: routes.search.value,
  name: 'Stacks',
  iconName: 'sidebar-process',
  allowedRoles: [...ADMIN_ROLES, AuthRoleNameEnum.PLAYER],
  allowedProfileTypes: ['coach', 'player']
}

// The Organization's Content Item library — Drills managed on their own and placed
// into Stacks. An authoring surface, so coach roles only; a Player reaches the
// same Drills through the Stacks they read.
const contentItemsRoutes: SidebarItem = {
  path: routes.contentItems.value,
  name: 'Content Items',
  iconName: 'sidebar-library',
  allowedRoles: ADMIN_ROLES,
  allowedProfileTypes: ['coach']
}

// The two placeholders deliberately carry no `allowedRoles`: `useRoleRouteGuard`
// turns every leaf with `allowedRoles` into a gated path, and these paths have no
// page behind them yet. Leaving them out keeps the guard table honest and stops a
// future real page from inheriting a role gate nobody chose.
const planComponentsRoutes: SidebarItem = {
  path: '/plan-components',
  name: 'Plan Components',
  iconName: 'material-symbols_bookmark-outline',
  isDisabled: true,
  allowedProfileTypes: ['coach']
}

const mediaResourceRoutes: SidebarItem = {
  path: '/media-resource',
  name: 'Media & Resource',
  iconName: 'perm_media_black_24dp',
  isDisabled: true,
  allowedProfileTypes: ['coach']
}

// Same destination as the legacy "User Groups" entry, relabelled for the new
// Organization → Team navigation.
const adminPlayerGroupsRoutes: SidebarItem = {
  path: routes.groups.value,
  name: 'Player Groups',
  iconName: 'ic_outline-player-groups',
  allowedRoles: ORG_ADMIN_ROLES,
  allowedProfileTypes: ['coach']
}

// Same destination as the legacy "Training Assignments" entry, relabelled.
const adminAssignmentsRoutes: SidebarItem = {
  path: routes.plans.trainingAssignments.value,
  name: 'Assignments',
  iconName: 'training-plan',
  featureFlag: EFeatureFlags.WORK_PLANS,
  allowedRoles: ADMIN_ROLES,
  allowedProfileTypes: ['coach']
}

// Same destination as `reportRoutes`, cloned only so the clipboard icon stays out
// of the basic-user / play-profile sidebar, which keeps the bar-chart icon. The
// Owner-only guard is inherited from `reportRoutes` via the spread.
const adminReportsRoutes: SidebarItem = {
  ...reportRoutes,
  iconName: 'ic_outline-content-paste'
}

// Top call-to-action above Dashboard, pointing at the Organization page (its
// membership, administrators, information, settings and billing tabs). Exported so
// LeftSidebar can render it outside the nav list.
//
// Deliberately carries no `allowedRoles`: it is not part of `adminSidebarItems`, so
// `useRoleRouteGuard` never sees it, and adding one here would read as a route gate
// that does not exist. Visibility is gated by `canManageOrg` (admin membership) in
// LeftSidebar; the route itself is gated through the sidebar entry that owns
// `routes.organization.value`.
export const manageOrgItem: SidebarItem = {
  path: routes.organization.value,
  name: 'Manage your Org',
  iconName: 'player-grid-view'
}

// Pinned to the bottom of the sidebar, below the scrollable nav.
export const helpItem: SidebarItem = {
  path: '/help',
  name: 'Help',
  iconName: 'ic_baseline-help',
  isDisabled: true
}

const adminContentGroup: SidebarItem = {
  path: '/',
  name: 'Content',
  iconName: '',
  allowedProfileTypes: ['coach'],
  children: [
    stacksRoutes,
    contentItemsRoutes,
    draftRoutes,
    plansRoutes,
    collectionRoutes,
    // Archived Stacks and Collections are content too, and Restore/Delete live
    // only on that page — without the entry an archived Stack is unreachable.
    archiveRoutes,
    planComponentsRoutes,
    mediaResourceRoutes
  ]
}

const adminTeamGroup: SidebarItem = {
  path: '/',
  name: 'Team & Group',
  iconName: '',
  allowedProfileTypes: ['coach'],
  children: [teamsRoutes, adminPlayerGroupsRoutes]
}

const adminOperationsGroup: SidebarItem = {
  path: '/',
  name: ERouteName.OPERATION,
  iconName: '',
  allowedProfileTypes: ['coach'],
  children: [scheduleRoutes, adminAssignmentsRoutes, adminReportsRoutes]
}

const settingRoutes: SidebarItem = {
  path: routes.setting.value,
  name: ERouteName.SETTING,
  iconName: 'sidebar-setting',
  // Settings group requires admin-level rank or higher; OWNER also covered.
  allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN],
  allowedProfileTypes: ['coach'],
  children: [
    {
      path: routes.setting.metric.value,
      name: ERouteName.METRIC,
      iconName: 'sidebar-document-type',
      allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN],
      allowedProfileTypes: ['coach']
    },
    {
      path: routes.setting.iconBuilder.value,
      name: ERouteName.ICON_BUILDER,
      iconName: 'sidebar-icon-builder',
      allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN],
      allowedProfileTypes: ['coach']
    },
    {
      path: routes.setting.tag.value,
      name: ERouteName.TAG,
      iconName: 'sidebar-tag',
      allowedRoles: [AuthRoleNameEnum.OWNER, AuthRoleNameEnum.ADMIN],
      allowedProfileTypes: ['coach']
    }
    // Billing entry temporarily hidden from the sidebar (page + route stay live).
    // {
    //   path: routes.setting.billing.value,
    //   name: ERouteName.BILLING,
    //   iconName: 'sidebar-setting',
    //   allowedRoles: [AuthRoleNameEnum.OWNER],
    //   allowedProfileTypes: ['coach']
    // }
  ]
}

// The admin SETTINGS section (Metrics / Icon Builder / Tags)
// was dropped from the Org Admin sidebar. `settingRoutes` stays alive for the
// basic-user sidebar below and the /setting/* routes still resolve directly.

//* INFO: Temporarily hide
// const processDraftsRoutes: SidebarItem = {
//   path: '/process-drafts',
//   name: 'Process Drafts',
//   icon: 'las la-sticky-note'
// }

// const quizRoutes: SidebarItem = {
//   path: '/quizzes',
//   name: 'Quizzes',
//   icon: 'las la-school'
// }

// const trainingRoutes: SidebarItem = {
//   path: '/training',
//   name: 'Training',
//   icon: 'las la-industry'
// }

const knowHowRoutes = [
  dashboardRoutes,
  scheduleRoutes,
  playerAssignmentRoutes,
  // Ahead of Teams so a Player's Stacks entry lands in one of the four mobile
  // bottom-bar slots instead of behind More — reading published Stacks is the
  // point of giving them the list at all.
  stacksRoutes,
  teamsRoutes,
  draftRoutes,
  collectionRoutes,
  plansRoutes,
  trainingAssignmentRoutes,
  archiveRoutes,
  reportRoutes,
  userRoutes,
  groupRoutes,
  settingRoutes
]

const adminRoutes: SidebarItem[] = [dashboardRoutes, adminContentGroup, adminTeamGroup, adminOperationsGroup]

//* INFO: Temporarily hide
// const groupRoutes: SidebarItem = {
//   path: '/groups',
//   name: 'Groups',
//   icon: 'las la-users',
//   header: 'Manage'
// }

// const workflowRoutes: SidebarItem = {
//   path: '/workflows',
//   name: 'Workflows',
//   icon: 'las la-network-wired'
// }

// const tagRoutes: SidebarItem = {
//   path: '/tags',
//   name: 'Tags',
//   icon: 'las la-tags'
// }

// const courseRoutes: SidebarItem = {
//   path: '/courses',
//   name: 'Courses',
//   icon: 'las la-graduation-cap'
// }

// const quizManagementRoutes: SidebarItem = {
//   path: '/quiz-management',
//   name: 'Quizzes',
//   icon: 'uil-home-alt'
// }

// const locationRoutes: SidebarItem = {
//   path: '/locations',
//   name: 'Locations',
//   icon: 'las la-map-marker-alt'
// }

//* INFO: Temporarily hide
const manageRoutes: SidebarItem[] = [
  // groupRoutes,
  // userRoutes,
  // workflowRoutes,
  // tagRoutes,
  // courseRoutes,
  // quizManagementRoutes,
  // locationRoutes
]

export const sidebarItems = [...knowHowRoutes, ...manageRoutes]
export const adminSidebarItems = adminRoutes
